/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {Asset, Device} from '@ion/core/src/types/entities';
import {createOptions, Options} from '../components/menus/Dropdown';

/**
 * Returns a non-nullish label string for the specified entity.
 */
export const getEntityLabel = (
  entity: Asset | Device,
  fallbackToId: boolean = false
): string => (
  String(
    entity?.name ||
    entity?.deviceName ||
    entity?.esn ||
    entity?.serialNumber ||
    (fallbackToId && entity?.id) ||
    translate('common:notAvailable')
  )
);

/**
 * Generates a list of dropdown labels and values based on the specified Asset or
 * Device collection.
 */
export const getOptions = (
  items: Asset[] | Device[],
  fallbackToId: boolean = false
): Options[] => {
  if (items?.length) {
    return createOptions({
      items     : items,
      valProp   : 'id',
      labelProp : 'name',
      augmentor : (item: Asset | Device, option: { value: number; label: string; }) => {
        return {
          ...option,
          label : getEntityLabel(item, fallbackToId),
          id    : item.id
        };
      }
    });
  }
  return [];
};

/**
 * Returns unique values from the specified Asset or Device collection.
 */
export const getUniqItems = (items: Asset[] | Device[]) => {
  return items?.reduce((unique, attr) => {
    if (!unique.some(({id}) => id === attr.id)) {
      unique.push(attr);
    }
    return unique;
  }, []);
};

/**
 * Returns unique default values from the specified Asset or Device collection.
 */
export const getValues = (
  defaultValues: Asset[] | Device[],
  fallbackToId: boolean = false
) => {
  return getUniqItems(defaultValues.map((val: Asset | Device) => {
    const {id} = val;
    return {
      label : getEntityLabel(val, fallbackToId),
      value : id,
      id
    };
  }));
};
